import moment from 'moment';

/**
 * The order filter's field registry, and the one place that knows how each
 * field becomes part of `GetOrdersInput2`.
 *
 * The panel used to collect roughly two dozen of the ~50 input fields, and the
 * conversion lived inside `handleStocksOrderFilter` as a long destructure —
 * which is how `assetKey` came to be collected by the form and never sent, and
 * how `statementUrl` and `historyUrl` came to be read from filter values the
 * panel never set. Declaring each field once, with its type, means a field
 * added to the panel is sent, labelled and saved without three separate edits.
 *
 * What is deliberately NOT here: `status`, `openOrders`, `hold` and
 * `includeOrdersWithElapsedExpiry` are decided by the active tab, and
 * `search`, `page`, `limit` and `paginate` by the page toolbar. This builder
 * returns a fragment that the caller merges around that logic, never over it.
 *
 * Excluded on purpose: `companyId`, deprecated in favour of `instrumentId`;
 * `newFirst`, owned by the page; and `brokerFormat` and
 * `recipients`, which the export modal collects itself — `recipients` is
 * `[String!]` of email addresses there, with daba-address validation, so a
 * second control here would fight it.
 *
 * `exportOnly` follows the schema's own notes. Eight fields are documented as
 * "only available when exporting orders to broker sheet" or "…for broker
 * backoffice", so they are collected by the export form and never sent with a
 * queue request, where they would do nothing.
 */

/** How a form value is turned into its API value. */
const AS = {
  /** Straight through — enums, ids, strings. */
  RAW: 'RAW',
  /** Float. The panel collects these through InputNumber, which can still
   *  hand back a string when a user pastes. */
  NUMBER: 'NUMBER',
  /** DateTime, serialised the way the queue has always sent dates. */
  DATE: 'DATE',
  /** Boolean. Only sent when explicitly set, so "not set" and "false" stay
   *  distinguishable — `placed: false` means something different from no
   *  opinion about placement. */
  BOOLEAN: 'BOOLEAN',
  /** [ID!] — an array of ids, empty arrays dropped. */
  ID_LIST: 'ID_LIST',
};

/**
 * Every field the panel owns: the form name, the input field it feeds, its
 * type, and the chip label.
 *
 * `field` differs from `name` only where the form has historically used
 * another word — `assetKey` for `assetClass`. Renaming the form field would
 * silently invalidate every saved view, so the mapping lives here instead.
 */
export const ORDER_FILTER_FIELDS = [
  // Order
  {name: 'id', field: 'id', as: AS.RAW, label: 'Order ID'},
  {name: 'txRef', field: 'txRef', as: AS.RAW, label: 'Transaction ref'},
  {name: 'type', field: 'type', as: AS.RAW, label: 'Type'},
  {name: 'direction', field: 'direction', as: AS.RAW, label: 'Direction'},
  {
    name: 'limitPriceSortDirection',
    field: 'limitPriceSortDirection',
    as: AS.RAW,
    label: 'Limit price sort',
  },

  // Parties
  {
    name: 'brokerId',
    field: 'brokerId',
    as: AS.RAW,
    label: 'Broker',
    exportOnly: true,
  },
  {
    name: 'brokerIds',
    field: 'brokerIds',
    as: AS.ID_LIST,
    label: 'Brokers',
    exportOnly: true,
  },
  {
    name: 'collectionId',
    field: 'collectionId',
    as: AS.RAW,
    label: 'Collection',
  },
  {name: 'orgId', field: 'orgId', as: AS.RAW, label: 'Organisation'},

  // Amounts
  {
    name: 'quantityGreaterThan',
    field: 'quantityGreaterThan',
    as: AS.NUMBER,
    label: 'Qty ≥',
  },
  {
    name: 'quantityLessThan',
    field: 'quantityLessThan',
    as: AS.NUMBER,
    label: 'Qty ≤',
  },
  {
    name: 'limitPriceGreaterThan',
    field: 'limitPriceGreaterThan',
    as: AS.NUMBER,
    label: 'Limit price ≥',
  },
  {
    name: 'limitPriceLessThan',
    field: 'limitPriceLessThan',
    as: AS.NUMBER,
    label: 'Limit price ≤',
  },
  {
    name: 'stopPriceGreaterThan',
    field: 'stopPriceGreaterThan',
    as: AS.NUMBER,
    label: 'Stop price ≥',
  },
  {
    name: 'stopPriceLessThan',
    field: 'stopPriceLessThan',
    as: AS.NUMBER,
    label: 'Stop price ≤',
  },

  // Venue & instrument
  {
    name: 'stockExchange',
    field: 'stockExchange',
    as: AS.RAW,
    label: 'Exchange',
  },
  {
    name: 'instrumentKey',
    field: 'instrumentKey',
    as: AS.RAW,
    label: 'Instrument type',
  },
  // The control is named `assetKey`; the input field is `assetClass`.
  {name: 'assetKey', field: 'assetClass', as: AS.RAW, label: 'Asset class'},
  {
    name: 'instrumentId',
    field: 'instrumentId',
    as: AS.RAW,
    label: 'Instrument',
  },
  // Deprecated in favour of `instrumentId`, but kept because reaching
  // `instrumentId` means picking an instrument key and asset class first,
  // which is a long way round when the ticker is already in hand.
  {name: 'symbol', field: 'symbol', as: AS.RAW, label: 'Symbol'},
  {
    name: 'assetCurrency',
    field: 'assetCurrency',
    as: AS.RAW,
    label: 'Asset currency',
  },
  {name: 'ipo', field: 'ipo', as: AS.BOOLEAN, label: 'IPO orders'},
  {name: 'derivative', field: 'derivative', as: AS.RAW, label: 'Derivative'},
  {
    name: 'derivatives',
    field: 'derivatives',
    as: AS.BOOLEAN,
    label: 'Derivatives',
  },

  // Dates
  {name: 'startDate', field: 'startDate', as: AS.DATE, label: 'From date'},
  {name: 'endDate', field: 'endDate', as: AS.DATE, label: 'To date'},
  {
    name: 'startFillDate',
    field: 'startFillDate',
    as: AS.DATE,
    label: 'Filled from',
  },
  {name: 'endFillDate', field: 'endFillDate', as: AS.DATE, label: 'Filled to'},
  {
    name: 'startSettlementDate',
    field: 'startSettlementDate',
    as: AS.DATE,
    label: 'Settled from',
  },
  {
    name: 'endSettlementDate',
    field: 'endSettlementDate',
    as: AS.DATE,
    label: 'Settled to',
  },
  {
    name: 'startExpiryDate',
    field: 'startExpiryDate',
    as: AS.DATE,
    label: 'Expiry from',
  },
  {
    name: 'endExpiryDate',
    field: 'endExpiryDate',
    as: AS.DATE,
    label: 'Expiry to',
  },

  // Flags
  {name: 'placed', field: 'placed', as: AS.BOOLEAN, label: 'Placed'},
  {name: 'bundle', field: 'bundle', as: AS.BOOLEAN, label: 'Bundle'},
  {
    name: 'assetPriceOverFloated',
    field: 'assetPriceOverFloated',
    as: AS.BOOLEAN,
    label: 'Overfloated',
  },
  {
    name: 'collection',
    field: 'collection',
    as: AS.BOOLEAN,
    label: 'Collection orders',
  },
  {
    name: 'affiliateOrder',
    field: 'affiliateOrder',
    as: AS.BOOLEAN,
    label: 'Affiliate',
  },
  {
    name: 'settledBySystem',
    field: 'settledBySystem',
    as: AS.BOOLEAN,
    label: 'Settled by system',
  },
  {
    name: 'includePendingOrders',
    field: 'includePendingOrders',
    as: AS.BOOLEAN,
    label: 'Include pending',
  },
  {
    name: 'backoffice',
    field: 'backoffice',
    as: AS.BOOLEAN,
    label: 'Back office',
    exportOnly: true,
  },

  // Export only. The two URLs are read by the broker export path, which has
  // always destructured them from the filter values without any control
  // setting them.
  {
    name: 'key',
    field: 'key',
    as: AS.RAW,
    label: 'Export key',
    exportOnly: true,
  },
  {
    name: 'statementUrl',
    field: 'statementUrl',
    as: AS.RAW,
    label: 'Statement URL',
    exportOnly: true,
  },
  {
    name: 'historyUrl',
    field: 'historyUrl',
    as: AS.RAW,
    label: 'History URL',
    exportOnly: true,
  },
];

export const ORDER_FILTER_LABELS = ORDER_FILTER_FIELDS.reduce(
  (labels, entry) => ({...labels, [entry.name]: entry.label}),
  {},
);

/** Fields rendered as a date, for the chip formatter. */
export const ORDER_FILTER_DATE_KEYS = ORDER_FILTER_FIELDS.filter(
  entry => entry.as === AS.DATE,
).map(entry => entry.name);

const isSet = value =>
  !(
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );

const toNumber = value => {
  if (typeof value === 'number') return value;
  const parsed = Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(parsed) ? parsed : undefined;
};

const toDate = value => {
  const parsed = moment.isMoment(value) ? value : moment(value);
  return parsed.isValid() ? parsed.format() : undefined;
};

const convert = (entry, value) => {
  switch (entry.as) {
    case AS.NUMBER:
      return toNumber(value);
    case AS.DATE:
      return toDate(value);
    case AS.BOOLEAN:
      return typeof value === 'boolean' ? value : undefined;
    case AS.ID_LIST:
      return Array.isArray(value) ? value.filter(Boolean) : undefined;
    default:
      return value;
  }
};

/**
 * Turns panel values into the part of `GetOrdersInput2` the panel owns.
 *
 * Only fields that are actually set appear, so merging this over the tab's own
 * input never overwrites a tab decision with an empty value.
 */
export const buildOrderFilterInput = (values = {}, {isExport = false} = {}) => {
  const input = {};
  ORDER_FILTER_FIELDS.forEach(entry => {
    if (entry.exportOnly && !isExport) return;
    const raw = values[entry.name];
    if (!isSet(raw)) return;
    const converted = convert(entry, raw);
    if (isSet(converted)) input[entry.field] = converted;
  });
  return input;
};

/**
 * Brings stored filter values back to what the form expects.
 *
 * A saved view is JSON, so its dates come back as ISO strings while antd's
 * DatePicker only accepts moments. Without this, applying a saved view then
 * opening the panel threw on the first date field.
 */
export const reviveFilterValues = (stored = {}) => {
  const revived = {...stored};
  ORDER_FILTER_DATE_KEYS.forEach(key => {
    if (!isSet(revived[key])) return;
    const parsed = moment(revived[key]);
    revived[key] = parsed.isValid() ? parsed : undefined;
  });
  return revived;
};

/** True when a value would appear in the request. */
export const hasFilterValue = isSet;
